/**
 * KRA Photo Journal – Kiosk Gate
 *
 * Lightweight location check for kiosk (iPad) mode.
 * No login required — just verifies the device is at the shop.
 *
 * Shows:
 *   - Spinner while checking
 *   - "Không thể truy cập" if location fails
 *   - Calls onEnter() if location passes (parent renders the app)
 */

import React, { useEffect, useState } from "react";

const API_BASE = (import.meta.env["VITE_API_BASE_URL"] as string | undefined) ?? "/api";

interface KioskGateProps {
  onEnter: () => void;
}

async function requestGPS(): Promise<GeolocationCoordinates | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) { resolve(null); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(pos.coords),
      () => resolve(null),
      { timeout: 10_000, maximumAge: 60_000 }
    );
  });
}

async function checkLocation(lat?: number, lng?: number): Promise<boolean> {
  const body: Record<string, unknown> = {};
  if (lat !== undefined) { body.lat = lat; body.lng = lng; }
  const resp = await fetch(`${API_BASE}/auth/check-location`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await resp.json()) as { allowed: boolean };
  return data.allowed;
}

export function KioskGate({ onEnter }: KioskGateProps) {
  const [status, setStatus] = useState<"checking" | "denied">("checking");

  useEffect(() => {
    (async () => {
      try {
        // 1. IP check first (fast, no prompt)
        const allowed = await checkLocation();
        if (allowed) { onEnter(); return; }

        // 2. GPS check
        const coords = await requestGPS();
        if (coords) {
          const gpsAllowed = await checkLocation(coords.latitude, coords.longitude);
          if (gpsAllowed) { onEnter(); return; }
        }

        setStatus("denied");
      } catch {
        setStatus("denied");
      }
    })();
  }, [onEnter]);

  if (status === "checking") {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl mb-3 animate-pulse">📍</div>
          <p className="text-white/40 text-sm">Đang kiểm tra vị trí…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-4">
      <div className="bg-white/5 rounded-2xl p-8 text-center max-w-xs">
        <div className="text-3xl mb-4">🔒</div>
        <h2 className="text-white font-medium mb-2">Không thể truy cập</h2>
        <p className="text-white/50 text-sm leading-relaxed mb-5">
          Màn hình này chỉ hoạt động trong studio KRA.<br />
          Vui lòng kết nối WiFi studio.
        </p>
        <button
          onClick={() => { setStatus("checking"); window.location.reload(); }}
          className="px-5 py-2 bg-white/10 rounded-xl text-white/70 text-sm hover:bg-white/15 transition-colors"
        >
          Thử lại
        </button>
      </div>
    </div>
  );
}
